import { type MutableRefObject, useCallback, useMemo, useRef } from 'react';
import isNumber from 'lodash/isNumber';
import { useDebounce, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import type { BaseRowData } from '@adv-frontend/data-provider';
import { type GridColumnSizingState, type GridPlainColumnDefinition, useGridColumnSizing } from '@adv-frontend/grid';
import { tryParseJSON } from '@adv-frontend/yastore-modules/lib';
import { saveToStorageColumnSizing } from '../../../lib/grid';
import { type GridSizesStorageKey, persistentStorage } from '../../../lib/storage';

/**
 * Размерность
 * number - фиксированный размер
 * [number] - минимальный размер
 * [number, number] - минимальный и максимальный размер
 */
export type AdaptiveSize = number | [number] | [number, number];

/**
 * Конфиг для размерности столбцов
 * ключ - идентификатор столбца
 */
export type AdaptiveSizeConfig = Record<string, AdaptiveSize>;

/**
 * Минимальный размер колонки
 */
const MIN_SIZE = 40;

type SetWidthOptions = {
    /**
     * Пересчитать ширины из конфига `sizes`, не подтягивая сохранённые значения.
     * Нужно при смене набора видимых колонок, чтобы сбросить растянутую последнюю колонку.
     */
    ignoreStorage?: boolean;
};

/*
 * Хук пытается подогнать размеры колонок под размер контейнера
 * ! Не умеет в группы колонок
 *
 * @returns rect - ширина и высота для виртуализации грида, columnSizing - состояние размерности колонок для грида
 */
export function useGridColumnAdaptiveSizing<Row extends BaseRowData>({
    sizes,
    columns,
    containerRef = { current: null },
    storageKey,
}: {
    sizes: AdaptiveSizeConfig;
    columns: GridPlainColumnDefinition<Row, string>[];
    containerRef?: MutableRefObject<HTMLElement | null>;
    storageKey: GridSizesStorageKey;
}) {
    const filteredSizes = useMemo<AdaptiveSizeConfig>(() => {
        const filteredSizes: AdaptiveSizeConfig = {};

        columns.forEach(({ id }) => {
            if (sizes[id]) {
                filteredSizes[id] = sizes[id];
            }
        });

        return filteredSizes;
    }, [columns, sizes]);

    const { normalizeSizes, initialSizes } = useMemo(() => {
        const storageItem = tryParseJSON<Record<string, number>>(persistentStorage.getItem(storageKey));

        const initialSizes: GridColumnSizingState = {};
        const normalizeSizes = columns.reduce<AdaptiveSizeConfig>(
            (acc, { id, size }) => {
                if (!acc[id]) {
                    acc[id] = size || [MIN_SIZE];
                }

                const s = acc[id];

                if (storageItem && isNumber(storageItem[id])) {
                    initialSizes[id] = storageItem[id];
                } else if (isNumber(s)) {
                    initialSizes[id] = s;
                } else if (Array.isArray(s)) {
                    initialSizes[id] = s[0];
                }

                return acc;
            },
            { ...filteredSizes }
        );

        return { initialSizes, normalizeSizes };
    }, [columns, filteredSizes, storageKey]);

    const { columnSizing, setColumnSizing: originalSetColumnSizing } = useGridColumnSizing(initialSizes);

    const refColumnSizing = useRef(columnSizing);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    refColumnSizing.current = columnSizing;

    const handleSetColumnSizing = useCallback(
        (changedValue: GridColumnSizingState | ((columnSizing: GridColumnSizingState) => GridColumnSizingState)) => {
            const newValue = typeof changedValue === 'function' ? changedValue(refColumnSizing.current) : changedValue;
            const totalSize = Object.values(newValue).reduce((acc, size) => acc + size, 0);

            const sizesToPersist = { ...newValue };

            // Если из-за сделанного изменения таблица перестаёт умещаться в контейнер, растягиваем последнюю колонку
            const lastColumnId = columns[columns.length - 1]?.id;

            if (lastColumnId && containerRef.current && containerRef.current.clientWidth > totalSize) {
                newValue[lastColumnId] += containerRef.current.clientWidth - totalSize;
            }

            originalSetColumnSizing(newValue);

            // Сохраняем данные в 'storage' на изменение размеров (ресайс колонок)
            saveToStorageColumnSizing(sizesToPersist, storageKey);
        },
        [originalSetColumnSizing, storageKey, columns, containerRef]
    );

    const setWidth = useDebounce((options?: SetWidthOptions) => {
        if (!containerRef?.current || columns.length === 0) {
            return;
        }

        const width = containerRef.current.clientWidth;

        handleSetColumnSizing(() => {
            const storageItem = options?.ignoreStorage
                ? null
                : tryParseJSON<Record<string, number>>(persistentStorage.getItem(storageKey));

            let containerWidth = width;
            let minWidth = 0;
            const minSizes: Record<string, number> = {};
            let calculatedSizes: [string, number, number][] = [];
            const sizes = Object.entries(normalizeSizes).reduce<Record<string, number>>((acc, [id, size]) => {
                if (storageItem && isNumber(storageItem[id])) {
                    acc[id] = storageItem[id];
                    minSizes[id] = storageItem[id];
                    containerWidth -= storageItem[id];
                } else if (isNumber(size)) {
                    acc[id] = size;
                    minSizes[id] = size;
                    containerWidth -= size;
                } else {
                    calculatedSizes.push([id, size[0], size[1] ?? Infinity]);
                    minWidth += size[0];
                    minSizes[id] = size[0];
                }

                return acc;
            }, {});

            if (minWidth >= containerWidth) {
                return minSizes;
            }

            const ratio = containerWidth / minWidth;

            minWidth = 0;

            calculatedSizes = calculatedSizes.filter(([id, minSize, maxSize]) => {
                const maxWidth = minSize * ratio;

                if (maxWidth >= maxSize) {
                    sizes[id] = maxSize;
                    containerWidth -= maxSize;

                    return false;
                }

                minWidth += minSize;

                return true;
            });

            const newRatio = containerWidth / minWidth;

            calculatedSizes.forEach(([id, minSize]) => {
                sizes[id] = minSize * newRatio;
            });

            return sizes;
        });
    }, 16);

    const columnIdsKey = useMemo(() => columns.map(({ id }) => id).join('\0'), [columns]);
    const prevColumnIdsKeyRef = useRef(columnIdsKey);
    const isInitialLayoutRef = useRef(true);

    useIsomorphicLayoutEffect(() => {
        if (isInitialLayoutRef.current) {
            isInitialLayoutRef.current = false;
            prevColumnIdsKeyRef.current = columnIdsKey;
            setWidth();

            return;
        }

        if (prevColumnIdsKeyRef.current === columnIdsKey) {
            return;
        }

        prevColumnIdsKeyRef.current = columnIdsKey;
        setWidth({ ignoreStorage: true });
        // setWidth пересоздаётся на каждом рендере (debounce); пересчёт нужен только при смене набора колонок
    }, [columnIdsKey]);

    return {
        columnSizing: {
            columnSizing,
            setColumnSizing: handleSetColumnSizing,
        },
    };
}
